import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { classifyServiceError } from './serviceErrors';
import { reportIncident } from './errorReporter';
import { publishServiceError } from './serviceErrorState';
import { metaTrack } from './metaPixel';
import { recordAttribution } from './attribution';
import { decideIsolation, LAST_UID_KEY } from './accountIsolation';
import { invalidateEntitlements } from './entitlements';

const API = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext();

export function formatError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || 'Something went wrong';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

// Professional Polish Audit (2026-02) — mirrors backend
// `core.entitlements.READ_ONLY_ROLES` / `is_read_only_role`. Used by
// dashboard pages to hide/disable mutation controls for a read-only
// account (the server also enforces this with a 403 — this is UI-only
// affordance, never the actual security boundary).
export const READ_ONLY_SECURITY_ROLES = ['read_only_viewer', 'auditor', 'driver_limited', 'personal_viewer'];
export function isReadOnlyAccount(user) {
  return READ_ONLY_SECURITY_ROLES.includes(user?.security_role);
}

// ─────────────────────────────────────────────────────────────────────────────
// Cross-domain token fallback store.
// In production the frontend and backend are on different registrable domains,
// so the HTTPOnly auth cookies are third-party and get silently dropped by
// Safari/Chrome tracking prevention — causing infinite 401 → /refresh →
// session_expired loops. As a resilient fallback we mirror the tokens in
// localStorage and send them via `Authorization: Bearer` / the refresh body.
// Cookies remain the PRIMARY mechanism; this only kicks in when they're blocked.
// Keys are `jtr_`-prefixed so wipePerAccountState() clears them on auth change.
// ─────────────────────────────────────────────────────────────────────────────
const ACCESS_KEY = 'jtr_access_token';
const REFRESH_KEY = 'jtr_refresh_token';

export function getStoredAccessToken() {
  try { return localStorage.getItem(ACCESS_KEY) || null; } catch { return null; }
}
export function getStoredRefreshToken() {
  try { return localStorage.getItem(REFRESH_KEY) || null; } catch { return null; }
}
export function storeAuthTokens(auth) {
  try {
    if (auth?.access_token) localStorage.setItem(ACCESS_KEY, auth.access_token);
    if (auth?.refresh_token) localStorage.setItem(REFRESH_KEY, auth.refresh_token);
  } catch { /* storage may be unavailable */ }
}
export function clearAuthTokens() {
  try {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch { /* noop */ }
  // Also clear the non-HttpOnly `jtr_session` hint cookie. If it lingers after
  // logout/expiry, every page load re-probes /api/auth/me → 401 → the "session
  // expired" nag fires again. Wiping it makes the next load take the fast
  // "not logged in" path with no probe and no toast.
  try {
    if (typeof document !== 'undefined') {
      document.cookie = 'jtr_session=; Path=/; Max-Age=0; SameSite=Lax';
    }
  } catch { /* noop */ }
}

// ─────────────────────────────────────────────────────────────────────────────
// Silent token refresh — on any 401, hit /api/auth/refresh and retry once.
// This single-flight design ensures parallel 401s don't fire 50 refresh calls.
// ─────────────────────────────────────────────────────────────────────────────
let refreshInFlight = null;

async function refreshAccessToken() {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      // Send the stored refresh token in the body as a fallback for when
      // the HTTPOnly refresh cookie was blocked cross-domain.
      const rt = getStoredRefreshToken();
      const r = await fetch(`${API}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(rt ? { refresh_token: rt } : {}),
      });
      if (!r.ok) return false;
      // Persist the rotated tokens for the header-based fallback path.
      try {
        const data = await r.json();
        if (data?._auth) storeAuthTokens(data._auth);
      } catch { /* body may be empty — cookie path still succeeded */ }
      return true;
    } catch { return false; }
    finally {
      // Allow next refresh after this one resolves (don't keep stale promise).
      setTimeout(() => { refreshInFlight = null; }, 0);
    }
  })();
  return refreshInFlight;
}

// Axios request interceptor — attach the Bearer fallback header on every
// request when we have a stored access token. Harmless when cookies work
// (the backend prefers the cookie); essential when cookies are blocked.
axios.interceptors.request.use((config) => {
  try {
    const at = getStoredAccessToken();
    if (at) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${at}`;
    }
  } catch { /* noop */ }
  return config;
});

// ─────────────────────────────────────────────────────────────────────────────
// Global fetch patch — attach the Bearer fallback header to every backend
// /api/* request that does not already carry an Authorization header. Dozens
// of components use raw fetch() (not authedFetch); without this they would
// 401 in true cross-domain production where third-party cookies are blocked,
// even though the auth session itself survives via the header path. Same-domain
// deployments are unaffected (the backend still prefers the cookie). The auth
// login/refresh endpoints are skipped (they manage tokens themselves).
// ─────────────────────────────────────────────────────────────────────────────
(function patchFetchForBearerFallback() {
  if (typeof window === 'undefined' || window.__jtrFetchPatched) return;
  const origFetch = window.fetch.bind(window);
  window.__jtrFetchPatched = true;

  // ── In-flight GET de-duplication ──────────────────────────────────────
  // Dashboard pages mount several independent components that each fetch the
  // SAME read-only endpoint on load (e.g. /api/cards was hit 9× on a single
  // /dashboard/cards load: CardsPage + DashboardLayout + SetupProgressPill +
  // SendTestReportPanel + StrictMode double-invoke). We coalesce identical
  // GET /api/* requests that are in-flight AT THE SAME TIME into one network
  // call; every caller gets an independent `response.clone()`. The entry is
  // removed the instant the request settles, so a later refetch (e.g. after a
  // mutation) is NEVER served stale — this is a pure overlap de-dup, not a
  // time-based cache. Requests carrying an AbortController signal are skipped
  // so per-caller cancellation semantics are preserved.
  const inflightGets = new Map();

  window.fetch = (input, init = {}) => {
    let url = '';
    try {
      url = typeof input === 'string' ? input : (input?.url || '');
      const isApi = !!API && url.startsWith(`${API}/api/`);
      const isAuthEndpoint = url.includes('/api/auth/login') || url.includes('/api/auth/refresh');
      if (isApi && !isAuthEndpoint) {
        const at = getStoredAccessToken();
        if (at) {
          const headers = new Headers(
            init.headers || (typeof input !== 'string' ? input.headers : undefined) || {},
          );
          if (!headers.has('Authorization')) {
            headers.set('Authorization', `Bearer ${at}`);
            init = { ...init, headers };
          }
        }
      }

      const method = (init.method
        || (typeof input !== 'string' ? input?.method : '')
        || 'GET').toUpperCase();
      const dedupable = isApi
        && method === 'GET'
        && !init.signal
        && !url.includes('/api/auth/');
      if (dedupable) {
        const existing = inflightGets.get(url);
        if (existing) return existing.then((r) => r.clone());
        const p = origFetch(input, init);
        inflightGets.set(url, p);
        const cleanup = () => { if (inflightGets.get(url) === p) inflightGets.delete(url); };
        p.then(cleanup, cleanup);
        return p.then((r) => r.clone());
      }
    } catch { /* noop — never let the patch break a request */ }
    return origFetch(input, init);
  };
})();


// Axios interceptor — auto-refresh on 401 then retry the original request.
axios.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const cfg = error?.config;
    const status = error?.response?.status;
    if (status !== 401 || !cfg || cfg._retried) throw error;
    // Don't recursively try to refresh the refresh endpoint itself, or login.
    // Also: the initial /api/auth/me bootstrap probe must NEVER raise the loud
    // "session expired" toast — a 401 there just means "not logged in" for an
    // anonymous visitor. Treat it silently (checkAuth's catch sets user=false).
    const url = cfg.url || '';
    if (url.includes('/api/auth/refresh') || url.includes('/api/auth/login')
        || url.includes('/api/auth/me')) throw error;
    cfg._retried = true;
    const ok = await refreshAccessToken();
    if (!ok) {
      // Iter 394g — Refresh failed. The session is genuinely dead.
      // Without this signal, callers (e.g. MeisaiConnect) surface a
      // raw "Not authenticated" error to the customer while the
      // wizard stays rendered, trapping them. Broadcast an event so
      // the AuthProvider can drop the user state — ProtectedRoute
      // then redirects to /login on the next render.
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('jtr:session-expired', {
            detail: { url, status },
          }));
        }
      } catch { /* noop */ }
      throw error;
    }
    return axios(cfg);
  }
);

/**
 * authedFetch(url, options) — drop-in replacement for `fetch` that:
 *   1. Always sends credentials.
 *   2. On 401, silently calls /api/auth/refresh and retries once.
 *   3. Returns the final Response (so callers can use res.ok / res.json()).
 *
 * Use this anywhere the codebase currently does `fetch(...)` against
 * a protected `/api/...` endpoint.
 */
/**
 * Build the Sentinel correlation headers for every authedFetch call.
 * Reads the IDs the VisitorBeacon writes to localStorage/sessionStorage
 * so any frontend → backend → replay correlation has a stable thread.
 */
function sentinelHeaders() {
  try {
    const h = {
      'x-jtr-request-id':
        (window.crypto?.randomUUID?.() ||
          `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`),
    };
    const sid = localStorage.getItem('jtr_visitor_id');
    const replay = sessionStorage.getItem('jtr_session_id');
    if (sid) h['x-jtr-session-id'] = sid;
    if (replay) h['x-jtr-replay-id'] = replay;
    return h;
  } catch {
    return {};
  }
}

// De-duplicate CONCURRENT identical GET requests (Professional Polish
// Audit, 2026-02, PPA-T09): several independent components on the same
// dashboard page each independently fetch the same read-only endpoint
// (/api/cards, /api/user/subscription, /api/user/meisai/status, ...) on
// mount, producing 2-4x redundant network+backend load per page load.
// This is an IN-FLIGHT-ONLY dedup, not a stale-data cache — once a request
// settles it's removed from the map immediately, so the very next call
// still fetches fresh data. Callers are unaffected: each still gets its
// own independently-readable Response via `.clone()`.
const _inflightGET = new Map(); // url -> Promise<Response|pseudo-error-object>

export async function authedFetch(input, init = {}) {
  const method = (init.method || 'GET').toUpperCase();
  const dedupeUrl = method === 'GET' && typeof input === 'string' ? input : null;
  if (dedupeUrl && _inflightGET.has(dedupeUrl)) {
    const shared = await _inflightGET.get(dedupeUrl);
    return typeof shared.clone === 'function' ? shared.clone() : shared;
  }
  const run = _authedFetchUncached(input, init);
  if (dedupeUrl) {
    _inflightGET.set(dedupeUrl, run);
    run.finally(() => { if (_inflightGET.get(dedupeUrl) === run) _inflightGET.delete(dedupeUrl); });
  }
  const res = await run;
  return typeof res.clone === 'function' ? res.clone() : res;
}

async function _authedFetchUncached(input, init = {}) {
  const at = getStoredAccessToken();
  const authHeader = at ? { Authorization: `Bearer ${at}` } : {};
  const headers = { ...sentinelHeaders(), ...authHeader, ...(init.headers || {}) };
  const opts = { credentials: 'include', ...init, headers };
  const url = typeof input === 'string' ? input : (input?.url || '');

  // Inner helper — wraps fetch so a network failure becomes a
  // 0-status pseudo-response we can classify uniformly.
  const doFetch = async () => {
    try {
      return await fetch(input, opts);
    } catch (netErr) {
      return { status: 0, ok: false, __networkError: netErr };
    }
  };

  let res = await doFetch();

  // Iter 323 — distinguish real gateway crashes from structured
  // "service unavailable" responses. Only retry + incident-report on
  //   • network error (status 0)
  //   • 502 Bad Gateway, 504 Gateway Timeout (proxy / worker crash)
  //   • 520-526 Cloudflare-origin errors
  // 503 with a JSON body carrying a `code` field (e.g. our
  // `automation_unavailable` from /api/user/meisai/pull-records-now)
  // is a planned, friendly state and MUST NOT alert support as DOWN.
  const isGatewayCrash = (
    res.status === 0
    || res.status === 502
    || res.status === 504
    || (res.status >= 520 && res.status <= 526)
  );
  if (isGatewayCrash) {
    const shouldSkipRetry = /\/api\/incidents\/report$/.test(url);
    if (!shouldSkipRetry) {
      await new Promise((r) => setTimeout(r, 700));
      res = await doFetch();
    }
    // After retry: if STILL failing, fire-and-forget an incident
    // report so support gets an early-warning email.
    const stillCrashing = (
      res.status === 0
      || res.status === 502
      || res.status === 504
      || (res.status >= 520 && res.status <= 526)
    );
    if (stillCrashing) {
      const info = classifyServiceError(res.status);
      // Publish to the in-app banner so logged-in users see "service is having issues".
      publishServiceError({
        classification: info.classification,
        status: res.status,
        title: info.title,
        detail: info.detail,
        advice: info.advice,
      });
      reportIncident({
        classification: info.classification,
        status: res.status,
        endpoint: url.replace(API || '', ''),
        message: res.__networkError ? String(res.__networkError.message || '') : `HTTP ${res.status}`,
        userEmail: (typeof window !== 'undefined' && window.__jtrUserEmail) || null,
      });
    }
  }

  if (res.status !== 401) return res;
  // Skip refresh for the refresh endpoint itself.
  if (url.includes('/api/auth/refresh') || url.includes('/api/auth/login')) return res;
  const ok = await refreshAccessToken();
  if (!ok) return res; // hand back the 401; caller decides what to do.
  // Rebuild headers with the freshly-rotated access token before retry.
  const newAt = getStoredAccessToken();
  const retryOpts = {
    ...opts,
    headers: { ...opts.headers, ...(newAt ? { Authorization: `Bearer ${newAt}` } : {}) },
  };
  res = await fetch(input, retryOpts);
  return res;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * Wipe per-account browser state (localStorage + sessionStorage) so a
   * different user signing in (or the same user signing back in after a
   * closure / wipe) never inherits the previous account's wizard step,
   * cached run-id, confetti flag, or anything else namespaced under
   * `jtr_*`. This also neutralises the production bug where a freshly
   * registered account immediately renders "Setup complete!" because
   * the previous account's setup-state survived in localStorage.
   *
   * Call sites:
   *   - login()    → after we know the new user.id
   *   - register() → after we know the new user.id
   *   - logout()   → unconditionally
   *
   * `keep_session_seed` lets the caller preserve the auth tokens
   * cookie path (we never store the JWT in localStorage anyway, but
   * future-proofing).
   */
  const wipePerAccountState = useCallback((reason = 'auth_change') => {
    try {
      const ls = window.localStorage;
      const ss = window.sessionStorage;
      // Preserve the cross-domain auth-token fallback keys from the
      // indiscriminate jtr_* wipe. Otherwise a normal page reload (which
      // mounts a fresh AuthProvider and looks like a null->user "switch")
      // would delete the very Bearer-header tokens that keep production
      // users logged in when third-party cookies are blocked. These keys
      // are cleared explicitly on logout / session-expired instead.
      const PRESERVE = new Set([ACCESS_KEY, REFRESH_KEY]);
      // Snapshot keys first; mutating storage during iteration is unsafe.
      const lsKeys = [];
      for (let i = 0; i < ls.length; i++) lsKeys.push(ls.key(i));
      lsKeys.forEach(k => { if (k && k.startsWith('jtr_') && !PRESERVE.has(k)) ls.removeItem(k); });

      const ssKeys = [];
      for (let i = 0; i < ss.length; i++) ssKeys.push(ss.key(i));
      ssKeys.forEach(k => { if (k && k.startsWith('jtr_')) ss.removeItem(k); });
      // Drop the session-scoped entitlements cache so a different user (or a
      // re-login) never inherits the previous account's feature gating.
      invalidateEntitlements();
      console.debug('[auth] wiped per-account state', { reason });
    } catch { /* ignore — storage may be unavailable in private mode */ }
  }, []);

  // Track the active user.id so we can detect identity changes between
  // mounts. Seeded from localStorage (LAST_UID_KEY, outside the jtr_ wipe
  // namespace) so a normal reload that restores the SAME user is NOT
  // mistaken for an account switch — which previously wiped all per-user
  // jtr_* state on every reload. See accountIsolation.js for the policy
  // and its safety argument.
  const activeUserIdRef = useState(() => {
    let seed = null;
    try { seed = window.localStorage.getItem(LAST_UID_KEY); } catch { /* ignore */ }
    return { current: seed };
  })[0];

  const setUserAndIsolate = useCallback((next) => {
    try {
      const nextId = next && typeof next === 'object' ? next.id : null;
      const decision = decideIsolation(activeUserIdRef.current, nextId);
      if (decision.wipe) wipePerAccountState('user_changed');
      activeUserIdRef.current = decision.activeUid;
      try {
        if (decision.clearPersist) window.localStorage.removeItem(LAST_UID_KEY);
        else if (decision.persistUid) window.localStorage.setItem(LAST_UID_KEY, decision.persistUid);
      } catch { /* ignore */ }
    } catch { /* ignore */ }
    setUser(next);
  }, [activeUserIdRef, wipePerAccountState]);

  const checkAuth = useCallback(async () => {
    // Short-circuit: if no session hint cookie is present, this browser
    // has never logged in (or has been logged out) — skip the /me probe
    // so public marketing pages stay silent (no 3× 401 console noise).
    const hasSessionHint = typeof document !== 'undefined'
      && document.cookie.split('; ').some(c => c.startsWith('jtr_session='));
    // Cross-domain fallback: in production the hint cookie is also a
    // blocked third-party cookie, so a stored access token (header path)
    // is an equally valid signal that this browser has a live session.
    if (!hasSessionHint && !getStoredAccessToken()) {
      setUserAndIsolate(false);
      setLoading(false);
      return null;
    }
    try {
      const { data } = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      setUserAndIsolate(data);
      return data;
    } catch (e) {
      // A 401 here means the session/hint is stale. Wipe the leftover
      // jtr_session cookie + tokens so the NEXT load takes the fast path
      // (no probe, no "session expired" toast). Network/other errors are
      // left alone so a transient blip can't nuke a valid session.
      if (e?.response?.status === 401) {
        try { clearAuthTokens(); } catch { /* noop */ }
      }
      setUserAndIsolate(false);
      return null;
    } finally {
      setLoading(false);
    }
  }, [setUserAndIsolate]);

  // checkAuth's no-session-hint fast path sets state synchronously on mount,
  // which is intentional for the auth bootstrap (decide logged-in vs anonymous
  // before first paint). Suppress the set-state-in-effect rule for this one.
  // eslint-disable-next-line
  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Iter 394g — Listen for the `jtr:session-expired` event dispatched
  // by the axios 401-interceptor when /api/auth/refresh has already
  // failed. Without this, a customer whose session JWT dies mid-flow
  // (e.g. on /api/user/meisai/connect) sees a raw "Not authenticated"
  // error while ProtectedRoute keeps them rendered on the dashboard.
  // With it, we drop user state to false, ProtectedRoute redirects
  // to /login on the next render, and toast a friendly explainer.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onSessionExpired = async () => {
      try {
        wipePerAccountState('session_expired');
        clearAuthTokens();
        setUserAndIsolate(false);
        try {
          const { toast } = await import('sonner');
          toast.error('Your session expired. Please sign in again.', {
            description: 'セッションの有効期限が切れました。再度サインインしてください。',
          });
        } catch { /* sonner not available */ }
      } catch { /* noop */ }
    };
    window.addEventListener('jtr:session-expired', onSessionExpired);
    return () => window.removeEventListener('jtr:session-expired', onSessionExpired);
  }, [setUserAndIsolate, wipePerAccountState]);

  const login = async (email, password) => {
    // Wipe BEFORE the network call so the brief moment between login()
    // resolving and React re-rendering can't surface stale dashboard state.
    wipePerAccountState('login');
    let data;
    try {
      const r = await axios.post(`${API}/api/auth/login`, { email, password }, {
        withCredentials: true,
        timeout: 12000,
      });
      data = r.data;
    } catch (err) {
      // Auto-retry once on 5xx / network errors so a transient platform
      // hiccup (Cloudflare 520, pod restart) doesn't bounce the user.
      const status = err?.response?.status;
      if (!status || status >= 500) {
        await new Promise((r) => setTimeout(r, 800));
        try {
          const r2 = await axios.post(`${API}/api/auth/login`, { email, password }, {
            withCredentials: true,
            timeout: 12000,
          });
          data = r2.data;
        } catch (err2) {
          const info = classifyServiceError(err2);
          // Fire-and-forget incident report so support sees the outage.
          reportIncident({
            classification: info.classification,
            status: info.status,
            endpoint: '/api/auth/login',
            message: err2?.message || `HTTP ${info.status}`,
            userEmail: email || null,
          });
          // Re-throw with a `serviceError` annotation the LoginPage can
          // render with a friendly headline + advice.
          err2.serviceError = info;
          throw err2;
        }
      } else {
        throw err;
      }
    }
    // Federation rejection (Iter 56): managed by Japan ETC Card. The
    // backend returns 200 with redirect_to + bilingual messaging — we
    // throw a marker error so the LoginPage can render the redirect.
    if (data && data.managed_by === 'japanetccard' && data.redirect_to) {
      const err = new Error(data.message_en || 'Account managed by Japan ETC Card');
      err.code = 'MANAGED_BY_ETC_CARD';
      err.redirect_to = data.redirect_to;
      err.message_ja = data.message_ja;
      err.message_en = data.message_en;
      throw err;
    }
    setUserAndIsolate(data);
    // Persist the token fallback AFTER setUserAndIsolate (which may wipe
    // jtr_* state on an account switch) so the tokens survive for the
    // header-based path when cookies are blocked cross-domain.
    storeAuthTokens(data?._auth);
    // Stash email for the global incident reporter so future failures
    // include the user identity.
    try { if (typeof window !== 'undefined') window.__jtrUserEmail = data?.email || null; } catch (_e) { /* noop */ }
    return data;
  };

  const register = async (body) => {
    wipePerAccountState('register');
    const { data } = await axios.post(`${API}/api/auth/register`, body, { withCredentials: true });
    setUserAndIsolate(data);
    try {
      metaTrack.completeRegistration(
        { email: body.email, external_id: data?.id || data?.user?.id },
        { account_type: body.account_type || '' },
      );
      const uid = data?.id || data?.user?.id || '';
      recordAttribution('account_created', { user_id: uid, account_type: body.account_type || '' });
      recordAttribution('trial_started', { user_id: uid, account_type: body.account_type || '' });
    } catch { /* tracking best-effort */ }
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    wipePerAccountState('logout');
    clearAuthTokens();
    setUserAndIsolate(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth, wipePerAccountState }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
